import { Link } from 'react-router-dom'
import { TopBar } from './TopBar'
import { useApp } from '../store/AppContext'
import { PERMISSION_META } from '../lib/permissions'
import type { AppPermission, Role } from '../types'
import { roleLabels } from './Avatar'

/**
 * Route-level access check.
 *
 * Hiding a nav button is only tidiness — this stops the screen rendering even
 * when someone types the URL. It is still a frontend control: the same
 * permission is listed in PERMISSION_BACKEND_MAP so the equivalent Supabase
 * Row Level Security policy can refuse the data itself once the backend lands.
 *
 * `roles` narrows a screen further, for pages that belong to one kind of team
 * member however generously the participant has shared things — writing a
 * shift handover being the case that exists today.
 */
export function RequirePermission({
  permission,
  roles,
  children,
}: {
  permission: AppPermission
  roles?: Role[]
  children: React.ReactNode
}) {
  const { can, currentUser } = useApp()
  if (!can(permission)) return <NoAccess permission={permission} />
  if (roles && !roles.includes(currentUser.role)) return <WrongRole roles={roles} />
  return <>{children}</>
}

/**
 * Not a permission problem: the person can see handovers, they just are not
 * the one who worked the shift. Point them at where they can read them.
 */
function WrongRole({ roles }: { roles: Role[] }) {
  const { can } = useApp()
  const who = roles.map((r) => `${roleLabels[r]}s`).join(' or ')
  const fallback = can('view_updates')
    ? { to: '/updates', label: 'Go to Team Updates' }
    : can('view_today')
      ? { to: '/today', label: 'Back to Today' }
      : { to: '/more', label: 'Back to More' }

  return (
    <div>
      <TopBar title="Not your screen" />
      <div className="px-4 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-card">
          <span className="text-3xl">📝</span>
          <p className="mt-3 text-sm font-bold text-slate-900">Handovers are written by {who}</p>
          <p className="mt-1 text-xs text-slate-500">
            The worker who was on the shift writes the note. Once it is saved it appears in Team Updates for
            everyone the participant has shared handovers with.
          </p>
          <Link
            to={fallback.to}
            className="mt-4 inline-block rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-bold text-white"
          >
            {fallback.label}
          </Link>
        </div>
      </div>
    </div>
  )
}

function NoAccess({ permission }: { permission: AppPermission }) {
  const { can } = useApp()
  const meta = PERMISSION_META[permission]
  // Today itself can be withheld, so never offer a link back to a locked screen.
  const fallback = can('view_today') ? { to: '/today', label: 'Back to Today' } : { to: '/more', label: 'Back to More' }

  return (
    <div>
      <TopBar title="Not shared with you" />
      <div className="px-4 py-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-card">
          <span className="text-3xl">🔒</span>
          <p className="mt-3 text-sm font-bold text-slate-900">{meta.label} isn’t shared with you</p>
          <p className="mt-1 text-xs text-slate-500">
            This participant decides who can {meta.description.toLowerCase()}. Ask them or their nominee if you
            need access.
          </p>
          <Link
            to={fallback.to}
            className="mt-4 inline-block rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-bold text-white"
          >
            {fallback.label}
          </Link>
        </div>
      </div>
    </div>
  )
}

